import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, Col, Row, Button, Alert } from "reactstrap";
import Breadcrumb from "../../../../components/Common/Breadcrumb";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { PropertySelector } from "./Components/PropertySelector";
import axios from "../../../api/axios";
import { getMasterSheetBasedOnPropertyId, getPropertyList } from "./Helpers/api";
import { FieldsForm } from "./Components/FieldsForm";

const INITIAL_FIELDS = [
    {
        key: 'unitNo',
        label: 'Unit No.',
        type: 'text',
        required: true,
        editable: false,
        deletable: false
    },
    {
        key: 'totalAreaSqft',
        label: 'Total Area (Sqft)',
        type: 'number',
        required: true,
        editable: true,
        deletable: false
    },
    {
        key: 'style',
        label: 'Style',
        type: 'text',
        required: true,
        editable: true,
        deletable: false,
    },
    {
        key: 'layout',
        label: 'Layout',
        type: 'text',
        required: true,
        editable: true,
        deletable: false
    },
    {
        key: 'tenants',
        label: 'Tenants',
        type: 'text',
        required: true,
        editable: true,
        deletable: false,
    },
    {
        key: 'securityDeposit',
        label: 'Security Deposit',
        type: 'number',
        required: true,
        editable: true,
        deletable: false
    },
    {
        key: 'targetRent',
        label: 'Target Rent',
        type: 'number',
        required: false,
        editable: true,
        deletable: false
    },
    {
        key: 'targetTelus',
        label: 'Target Telus',
        type: 'number',
        required: false,
        editable: true,
        deletable: true
    },
    {
        key: 'basicRent',
        label: 'Basic Rent',
        type: 'number',
        required: true,
        editable: true,
        deletable: false
    },
    {
        key: 'telus',
        label: 'Telus',
        type: 'number',
        required: false,
        editable: true,
        deletable: true
    },
    {
        key: 'surfaceStallFee',
        label: 'Surface Stall Fee',
        type: 'number',
        required: false,
        editable: true,
        deletable: true
    },
    {
        key: 'undergroundFee',
        label: 'Underground Fee',
        type: 'number',
        required: false,
        editable: true,
        deletable: true
    },
    {
        key: 'dogFee',
        label: 'Dog Fee',
        type: 'number',
        required: false,
        editable: true,
        deletable: false
    },
    {
        key: 'catFee',
        label: 'Cat Fee',
        type: 'number',
        required: false,
        editable: true,
        deletable: false
    },
    {
        key: 'leaseDetails',
        label: 'Lease Details',
        type: 'group',
        required: true,
        editable: true,
        deletable: true,
        fields: [
            {
                key: 'leaseStartDMY',
                label: 'Lease Start (D-M-Y)',
                type: 'date',
                required: true,
                editable: true,
                deletable: false
            },
            {
                key: 'leaseEndDMY',
                label: 'Lease End (D-M-Y)',
                type: 'date',
                required: true,
                editable: true,
                deletable: false
            },
        ],
    },
    {
        key: 'mailbox',
        label: 'Mailbox',
        type: 'group',
        editable: true,
        required: false,
        deletable: true,
        fields: [
            {
                key: 'module',
                label: 'Module',
                type: 'boolean',
                required: true,
                editable: false,
                deletable: false,
                defaultValue: true
            },
            {
                key: 'compartment',
                label: 'Compartment',
                type: 'boolean',
                required: true,
                editable: false,
                deletable: false,
                defaultValue: true
            },
        ],
    },

    // The CFO's expanded unit record (2026-10-13). On the sheet these fold away under
    // "More columns" (extraColumnGroups.js); the Rent Roll reads them by these keys.
    // All text, deliberately: an optional number left blank fails the Edit Tenant form's
    // "Must be a number" check, which would block saving every unit until it was filled.
    ...[
        ['floor', 'Floor', 'text'],
        ['bed', 'Bed', 'text'],
        ['bath', 'Bath', 'text'],
        ['affordableUnit', 'Affordable Unit', 'text', 'Yes or No'],
        ['unitCondition', 'Unit Condition', 'text', 'Excellent, Good, Fair or Poor'],
        ['scheduledRepairs', 'Scheduled Repairs', 'text'],
        ['insuranceProvider', 'Insurance Provider', 'text'],
        ['insuranceReviewed', 'Insurance Reviewed', 'text', 'Yes or No'],
        ['submeteringAccount', 'Submetering Account', 'text'],
        ['landlordCharges', 'Landlord Charges', 'text'],
        ['incentives', 'Incentives', 'text'],
        ['expectedActionAtLeaseEnd', 'Expected Action at End of Lease', 'text', 'Renewing, Not renewing, Month-to-month or Undecided'],
        ['tenantRating', 'Tenant Rating', 'text', 'Excellent, Good, Fair or Poor'],
    ].map(([key, label, type, description]) => ({
        key,
        label,
        type,
        required: false,
        editable: true,
        deletable: true,
        ...(description ? { description } : {}),
    })),

    {
        key: 'status',
        label: 'Status',
        type: 'text',
        required: false,
        editable: true,
        deletable: false
    },
    {
        key: 'comments',
        label: 'Comments',
        type: 'text',
        required: false,
        editable: true,
        deletable: false
    },
];

const MasterSheetFields = () => {
    // Get user data from localStorage
    const { userData = {} } = JSON.parse(localStorage.getItem("authUser") || '{"userData": {}}');

    // Component state
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [mastersheet, setMastersheet] = useState();
    const [filterData, setFilterData] = useState({
        property_id: "",
        property_name: "",
        building_name: "",
        building_list: []  // Added default empty array for building_list
    });

    // Computed property to validate selection
    const isValidSelection = !!filterData.property_id && (filterData.building_list?.length === 0 || !!filterData.building_name);

    // Load property list when component mounts
    useEffect(() => {
        if (userData?.id) {
            getPropertyList(userData)
                .then((filteredData) => {
                    if (filteredData) {
                        setFilterData(prev => ({
                            ...prev,
                            ...filteredData,
                            property_id: ""
                        }));
                    }
                })
                .catch(error => console.error("Error fetching property list:", error));
        }
    }, [userData?.id]);

    // Load mastersheet when property or building changes
    useEffect(() => {
        if (filterData?.property_id) {
            getMasterSheetBasedOnPropertyId({
                property_id: filterData.property_id,
                building_name: filterData.building_name
            })
                .then(({ data }) => {
                    if (data) {
                        setMastersheet(data);
                        setIsCreatingNew(false);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching master sheet:", error);
                    setMastersheet(undefined);
                });
        }
    }, [filterData?.property_id, filterData?.building_name]);

    // Handle the master sheet import/export actions
    const handleMasterSheetAction = async () => {
        try {
            const result = await Swal.fire({
                title: "Download Master Sheet template",
                text: `Do you want to download the ${filterData.property_name} Master Sheet?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Download",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                await handleDownloadMasterSheet();
            }
        } catch (error) {
            Swal.fire("Error", "An unexpected error occurred.", "error");
            console.error("Master sheet download error:", error);
        }
    };

    const handleDownloadMasterSheet = async () => {
        try {
            const response = await axios.get("/v2/master_sheet/layout/template", {
                params: {
                    property_id: filterData?.property_id,
                    building_name: filterData?.building_name
                },
                responseType: "blob"
            });
            const file = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(file, `MasterSheet.xlsx`);
            Swal.fire("Success!", "The Excel file has been downloaded.", "success");
        } catch (error) {
            Swal.fire("Error", "An error occurred during download.", "error");
            console.error("Download error:", error);
        }
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumb title="Home" breadcrumbItem="Master Sheet" />
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <PropertySelector
                                    action={handleMasterSheetAction}
                                    userData={userData}
                                    filterData={filterData}
                                    setFilterData={setFilterData}
                                    setIsCreatingNew={setIsCreatingNew}
                                />
                            </CardHeader>
                            <CardBody>
                                {!isValidSelection && (
                                    <Alert color="danger" className="mt-3 text-center">
                                        <strong>⚠️ Property Name and Building must be selected!</strong>
                                    </Alert>
                                )}

                                {isValidSelection && mastersheet === undefined && (
                                    <>
                                        {!isCreatingNew ? (
                                            <Alert color="warning" className="d-flex align-items-center justify-content-between p-3 border border-warning rounded">
                                                <div className="d-flex align-items-center">
                                                    <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                                    <strong>⚠️ No Layout Available for this Property</strong>
                                                </div>
                                                <Button
                                                    color={isCreatingNew ? "secondary" : "primary"}
                                                    className="fw-bold px-4"
                                                    onClick={() => setIsCreatingNew(!isCreatingNew)}
                                                >
                                                    <i className="bi bi-plus-lg me-1"></i> Create New Layout
                                                </Button>
                                            </Alert>
                                        ) : (
                                            <FieldsForm
                                                property_id={filterData.property_id || ''}
                                                building_name={filterData.building_name || ''}
                                                initialFields={INITIAL_FIELDS}
                                                isCreatingNew={isCreatingNew}
                                            />
                                        )}
                                    </>
                                )}

                                {isValidSelection && mastersheet !== undefined && !isCreatingNew && (
                                    <FieldsForm
                                        property_id={filterData.property_id || ''}
                                        building_name={filterData.building_name || ''}
                                        initialFields={mastersheet.fields}
                                        isCreatingNew={isCreatingNew}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export { MasterSheetFields };